import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AlertRelatedResponse, RelatedMatchReason } from '@/types/alertDetail';
import type { Severity } from '@/types/alerts';

const SEVERITY_BADGE: Record<Severity, string> = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-amber-100 text-amber-800 border-amber-200',
  Low: 'bg-blue-100 text-blue-800 border-blue-200',
  Watch: 'bg-slate-100 text-slate-700 border-slate-200',
  Normal: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const MATCH_REASON_TONE: Record<string, string> = {
  asset: 'bg-violet-100 text-violet-800 border-violet-200',
  card: 'bg-sky-100 text-sky-800 border-sky-200',
  attendant: 'bg-amber-100 text-amber-800 border-amber-200',
};

function matchReasonBadge(reason: RelatedMatchReason): JSX.Element {
  const tone = MATCH_REASON_TONE[reason] ?? 'bg-slate-100 text-slate-700 border-slate-200';
  return (
    <Badge variant="outline" className={cn('text-[10px] uppercase', tone)}>
      {reason}
    </Badge>
  );
}

interface RelatedAlertsSectionProps {
  query: UseQueryResult<AlertRelatedResponse, Error>;
}

/**
 * Section 6: related alerts on same asset/card/attendant in last 30 days.
 *
 * Each row links into the Fraud Center filtered to that alert; the
 * `match_reason` chip tells the investigator why the row showed up.
 */
export function RelatedAlertsSection({
  query,
}: RelatedAlertsSectionProps): JSX.Element {
  if (query.isLoading) {
    return (
      <section aria-labelledby="related-alerts">
        <h2 id="related-alerts" className="text-lg font-semibold">
          Related alerts (30d)
        </h2>
        <Skeleton className="mt-3 h-32 w-full" />
      </section>
    );
  }

  if (query.isError) {
    return (
      <section aria-labelledby="related-alerts">
        <h2 id="related-alerts" className="text-lg font-semibold">
          Related alerts (30d)
        </h2>
        <p className="mt-2 text-sm text-destructive">
          Failed to load related alerts: {query.error?.message ?? 'unknown error'}
        </p>
      </section>
    );
  }

  const related = query.data?.related ?? [];
  const total = query.data?.total_count ?? 0;

  if (related.length === 0) {
    return (
      <section aria-labelledby="related-alerts">
        <h2 id="related-alerts" className="text-lg font-semibold">
          Related alerts (30d)
        </h2>
        <p className="mt-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          No other alerts on this driver/vehicle/card in the last 30 days.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="related-alerts">
      <div className="flex items-baseline justify-between">
        <h2 id="related-alerts" className="text-lg font-semibold">
          Related alerts (30d)
        </h2>
        <span className="text-xs text-muted-foreground">
          {related.length} shown · {total} total
        </span>
      </div>
      <div className="mt-3 overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Rule</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Risk</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Match</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {related.map((r) => (
              <TableRow key={r.alert_id}>
                <TableCell className="text-xs">
                  <Link
                    to={`/fraud/${r.alert_id}`}
                    className="text-primary underline-offset-2 hover:underline"
                  >
                    {new Date(r.alert_time)
                      .toISOString()
                      .replace('T', ' ')
                      .slice(0, 19)}
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.rule_id}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn('text-xs', SEVERITY_BADGE[r.severity])}
                  >
                    {r.severity}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{r.risk_score}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className="text-xs">
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell>{matchReasonBadge(r.match_reason)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {total > related.length && (
        <p className="mt-2 text-xs text-muted-foreground">
          <Link to="/fraud" className="underline-offset-2 hover:underline">
            View all in Fraud Center →
          </Link>
        </p>
      )}
    </section>
  );
}
